import { useState, useEffect } from 'react';
import { X, Check, AlertCircle, Loader2, Database, ExternalLink, Key, Sparkles } from 'lucide-react';
import { apiRequest } from '@/lib/api';

interface DatabaseConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onConnectionSaved: () => void;
  initialMode?: Mode;
}

type DatabaseType = 'supabase' | 'postgresql' | 'mysql';
type Mode = 'choose' | 'create-new' | 'manual-entry' | 'setup-pat';
type CreateStep = 'configure' | 'creating' | 'success';

interface ConnectionConfig {
  url: string;
  anonKey: string;
  serviceRoleKey?: string;
}

interface Organization {
  id: string;
  name: string;
}

interface Region {
  value: string;
  label: string;
  flag: string;
}

export default function DatabaseConnectionModal({
  isOpen,
  onClose,
  projectId,
    onConnectionSaved,
  initialMode,
}: DatabaseConnectionModalProps) {
  // Mode and navigation
  const [mode, setMode] = useState<Mode>('choose');
  const [createStep, setCreateStep] = useState<CreateStep>('configure');
  
  // Manual entry state
  const [dbType, setDbType] = useState<DatabaseType>('supabase');
  const [config, setConfig] = useState<ConnectionConfig>({
    url: '',
    anonKey: '',
    serviceRoleKey: '',
  });
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);

  // PAT setup state
  const [hasPAT, setHasPAT] = useState(false);
  const [checkingPAT, setCheckingPAT] = useState(true);
  const [patInput, setPatInput] = useState('');
  const [savingPAT, setSavingPAT] = useState(false);

  // Create new project state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [projectName, setProjectName] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('us-east-1');
  const [dbPassword, setDbPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdProjectUrl, setCreatedProjectUrl] = useState('');


  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      checkPATStatus();
      loadExistingConnection();
    }
  }, [isOpen, projectId]);

  // Handle initialMode after PAT status is known
  useEffect(() => {
    if (isOpen && !checkingPAT && initialMode === 'create-new') {
      if (hasPAT) {
        setMode('create-new');
        loadOrganizationsAndRegions();
      } else {
        setMode('setup-pat');
      }
    }
  }, [isOpen, checkingPAT, hasPAT, initialMode]);
  const resetForm = () => {
    setMode('choose');
    setCreateStep('configure');
    setConfig({ url: '', anonKey: '', serviceRoleKey: '' });
    setTestResult(null);
    setPatInput('');
    setProjectName('');
    setDbPassword('');
    setSelectedOrg('');
    setCreateError(null);
    setCreatedProjectUrl('');
  };

  const checkPATStatus = async () => {
    setCheckingPAT(true);
    try {
      const response = await apiRequest<{ hasToken: boolean }>('get', '/database/supabase-pat/status');
      setHasPAT(response.hasToken);
    } catch (error) {
      setHasPAT(false);
    } finally {
      setCheckingPAT(false);
    }
  };

  const loadExistingConnection = async () => {
    try {
      const response = await apiRequest<any>('get', `/database/connections/${projectId}`);
      if (response.data) {
        setDbType(response.type);
        setTestResult({ success: response.status === 'connected' });
      }
    } catch (error) {
      // No existing connection, that's fine
    }
  };

  const handleSavePAT = async () => {
    if (!patInput.trim()) return;

    setSavingPAT(true);
    try {
      await apiRequest('post', '/database/supabase-pat', { accessToken: patInput.trim() });
      setHasPAT(true);
      setMode('create-new');
      await loadOrganizationsAndRegions();
    } catch (error: any) {
      setCreateError(error.message || 'Failed to save access token');
    } finally {
      setSavingPAT(false);
    }
  };

  const loadOrganizationsAndRegions = async () => {
    setLoadingOrgs(true);
    try {
      const [orgsResponse, regionsResponse] = await Promise.all([
        apiRequest<{ organizations: Organization[] }>('get', '/database/supabase/organizations'),
        apiRequest<{ regions: Region[] }>('get', '/database/supabase/regions'),
      ]);

      setOrganizations(orgsResponse.organizations);
      setRegions(regionsResponse.regions);

      if (orgsResponse.organizations.length > 0) {
        setSelectedOrg(orgsResponse.organizations[0].id);
      }
    } catch (error: any) {
      setCreateError(error.message || 'Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleCreateProject = async () => {
    if (!selectedOrg || !projectName.trim() || !dbPassword) {
      setCreateError('Please fill in all required fields');
      return;
    }

    setCreating(true);
    setCreateStep('creating');
    setCreateError(null);

    try {
      const response = await apiRequest<{ data: { projectRef: string; url: string; message: string } }>(
        'post',
        '/database/supabase/create-project',
        {
          projectId,
          organizationId: selectedOrg,
          name: projectName.trim(),
          region: selectedRegion,
          dbPassword,
        }
      );

      setCreatedProjectUrl(response.data.url);
      setCreateStep('success');
      
      // Auto-close after 3 seconds and notify parent
      setTimeout(() => {
        onConnectionSaved();
        onClose();
      }, 3000);
    } catch (error: any) {
      setCreateError(error.message || 'Failed to create Supabase project');
      setCreateStep('configure');
    } finally {
      setCreating(false);
    }
  };

  const handleTestManual = async () => {
    setIsTesting(true);
    setTestResult(null);

    try {
      const response = await apiRequest<{ success: boolean; error?: string }>(
        'post',
        '/database/test',
        { type: dbType, config }
      );
      setTestResult(response);
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.message || 'Connection test failed',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSaveManual = async () => {
    setIsSaving(true);

    try {
      await apiRequest('post', '/database/connections', {
        projectId,
        type: dbType,
        config,
      });

      onConnectionSaved();
      onClose();
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.message || 'Failed to save connection',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChooseCreateNew = () => {
    if (hasPAT) {
      setMode('create-new');
      loadOrganizationsAndRegions();
    } else {
      setMode('setup-pat');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 sticky top-0 bg-gray-900 z-10">
          <h2 className="text-lg font-semibold text-white">
            {mode === 'choose' && 'Connect Database'}
            {mode === 'setup-pat' && 'Setup Supabase Access'}
            {mode === 'create-new' && 'Create New Supabase Project'}
            {mode === 'manual-entry' && 'Database Connection'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* Mode: Choose */}
          {mode === 'choose' && (
            <div className="space-y-4">
              <p className="text-gray-300 text-sm">
                Choose how you'd like to set up your database connection:
              </p>

              <button
                onClick={handleChooseCreateNew}
                disabled={checkingPAT}
                className="w-full p-6 border-2 border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10 hover:border-blue-500/50 rounded-lg transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/30 transition-colors">
                    <Sparkles className="text-blue-400" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">
                      Create New Supabase Project
                    </h3>
                    <p className="text-gray-400 text-sm">
                      Automatically create a new Supabase project and connect it in seconds. Perfect for new projects.
                    </p>
                    {!hasPAT && !checkingPAT && (
                      <p className="text-yellow-400 text-xs mt-2 flex items-center gap-1">
                        <Key size={12} />
                        Requires one-time setup with your Supabase access token
                      </p>
                    )}
                  </div>
                </div>
              </button>

              <button
                onClick={() => setMode('manual-entry')}
                className="w-full p-6 border-2 border-gray-600 hover:border-gray-500 rounded-lg transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gray-600 transition-colors">
                    <Database className="text-gray-300" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">
                      I Have Existing Credentials
                    </h3>
                    <p className="text-gray-400 text-sm">
                      Manually enter connection details for an existing Supabase project or other database.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Mode: Setup PAT */}
          {mode === 'setup-pat' && (
            <div className="space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Key className="text-blue-400 mt-1" size={20} />
                  <div className="flex-1">
                    <h3 className="text-blue-300 font-medium mb-2">Supabase Personal Access Token Required</h3>
                    <p className="text-gray-300 text-sm mb-3">
                      To create Supabase projects automatically, we need your Personal Access Token (PAT). This is a one-time setup.
                    </p>
                    <ol className="text-sm text-gray-300 space-y-2 mb-3">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 font-mono">1.</span>
                        <span>Go to your <a href="https://supabase.com/dashboard/account/tokens" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline inline-flex items-center gap-1">Supabase Account Settings <ExternalLink size={12} /></a></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 font-mono">2.</span>
                        <span>Click "Generate new token" and give it a name (e.g., "Uncommonable IDE")</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 font-mono">3.</span>
                        <span>Copy the token and paste it below</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Personal Access Token
                </label>
                <input
                  type="password"
                  value={patInput}
                  onChange={(e) => setPatInput(e.target.value)}
                  placeholder="sbp_xxxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Your token is encrypted and stored securely
                </p>
              </div>

              {createError && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400">
                  <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{createError}</p>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => setMode('choose')}
                  className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-md transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSavePAT}
                  disabled={savingPAT || !patInput.trim()}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {savingPAT && <Loader2 size={16} className="animate-spin" />}
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Mode: Create New Project */}
          {mode === 'create-new' && (
            <div className="space-y-4">
              {createStep === 'configure' && (
                <>
                  {loadingOrgs ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="animate-spin text-blue-500" size={32} />
                      <span className="ml-3 text-gray-400">Loading organizations...</span>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Organization
                        </label>
                        <select
                          value={selectedOrg}
                          onChange={(e) => setSelectedOrg(e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Project Name *
                        </label>
                        <input
                          type="text"
                          value={projectName}
                          onChange={(e) => setProjectName(e.target.value)}
                          placeholder="my-awesome-project"
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Region
                        </label>
                        <select
                          value={selectedRegion}
                          onChange={(e) => setSelectedRegion(e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {regions.map((region) => (
                            <option key={region.value} value={region.value}>
                              {region.flag} {region.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Database Password *
                        </label>
                        <input
                          type="password"
                          value={dbPassword}
                          onChange={(e) => setDbPassword(e.target.value)}
                          placeholder="••••••••••••"
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Minimum 8 characters
                        </p>
                      </div>

                      {createError && (
                        <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400">
                          <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                          <p className="text-sm">{createError}</p>
                        </div>
                      )}

                      <div className="flex items-center gap-3 pt-2">
                        <button
                          onClick={() => setMode('choose')}
                          disabled={creating}
                          className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-md transition-colors disabled:opacity-50"
                        >
                          Back
                        </button>
                        <button
                          onClick={handleCreateProject}
                          disabled={creating || !projectName.trim() || !dbPassword || dbPassword.length < 8}
                          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {creating && <Loader2 size={16} className="animate-spin" />}
                          Create Supabase Project
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}

              {createStep === 'creating' && (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="animate-spin text-blue-500 mb-4" size={48} />
                  <p className="text-white font-medium text-lg mb-2">Creating your Supabase project...</p>
                  <p className="text-gray-400 text-sm">This will take about 5-10 seconds</p>
                </div>
              )}

              {createStep === 'success' && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                    <Check className="text-green-400" size={32} />
                  </div>
                  <p className="text-white font-medium text-xl mb-2">Project Created!</p>
                  <p className="text-gray-400 text-sm mb-4">Your database is ready and automatically connected</p>
                  <a
                    href={createdProjectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                  >
                    Open Supabase Dashboard <ExternalLink size={14} />
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Mode: Manual Entry */}
          {mode === 'manual-entry' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Database Type
                </label>
                <select
                  value={dbType}
                  onChange={(e) => setDbType(e.target.value as DatabaseType)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="supabase">Supabase</option>
                  <option value="postgresql" disabled>
                    PostgreSQL (Coming Soon)
                  </option>
                  <option value="mysql" disabled>
                    MySQL (Coming Soon)
                  </option>
                </select>
              </div>

              {dbType === 'supabase' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Project URL
                    </label>
                    <input
                      type="text"
                      value={config.url}
                      onChange={(e) => setConfig({ ...config, url: e.target.value })}
                      placeholder="https://xxxxx.supabase.co"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Anon Key
                    </label>
                    <input
                      type="password"
                      value={config.anonKey}
                      onChange={(e) => setConfig({ ...config, anonKey: e.target.value })}
                      placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Found in Supabase Dashboard → Settings → API
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Service Role Key (Optional)
                    </label>
                    <input
                      type="password"
                      value={config.serviceRoleKey}
                      onChange={(e) =>
                        setConfig({ ...config, serviceRoleKey: e.target.value })
                      }
                      placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Allows Claude to make database changes (bypasses RLS)
                    </p>
                  </div>
                </>
              )}

              {testResult && (
                <div
                  className={`flex items-start gap-2 px-4 py-3 rounded-md ${
                    testResult.success
                      ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                      : 'bg-red-500/10 border border-red-500/20 text-red-400'
                  }`}
                >
                  {testResult.success ? (
                    <>
                      <Check size={20} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium">Connection successful!</p>
                        <p className="text-sm opacity-80">
                          Database is ready to use with Claude
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium">Connection failed</p>
                        <p className="text-sm opacity-80">{testResult.error}</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => setMode('choose')}
                  className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-md transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleTestManual}
                  disabled={isTesting || !config.url || !config.anonKey}
                  className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isTesting && <Loader2 size={16} className="animate-spin" />}
                  Test Connection
                </button>

                <button
                  onClick={handleSaveManual}
                  disabled={isSaving || !testResult?.success}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving && <Loader2 size={16} className="animate-spin" />}
                  Save Connection
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
